import { ElementsMetadata } from 'src/utils/getElementsMetadata';

export enum FlagPostOptions {
  offTopicInappropriate = 'OFF_TOPIC_INAPPROPRIATE',
  spamExternalLink = 'SPAM_EXTERNAL_LINK',
  piiData = 'PII',
  duplicateIncorrectCategory = 'DUPLICATE_INCORRECT_CATEGORY',
  linksOutOfDate = 'LINKS_OUT_OF_DATE',
}

export enum PostType {
  reply = 'REPLY',
  post = 'POST',
  op = 'OP',
}

export enum PostState {
  draft = 'DRAFT',
  active = 'ACTIVE',
  processing = 'PROCESSING',
  deleted = 'DELETED',
  archived = 'ARCHIVED',
  closed = 'CLOSED',
}

export enum PostErrors {
  voteSubmission = 'voteSubmission',
  flagPost = 'flagPost',
  fetchPreviousReplies = 'fetchPreviousReplies',
  fetchNextReplies = 'fetchNextReplies',
  copyPost = 'copyPost',
  deletePost = 'deletePost',
  deleteDraft = 'deleteDraft',
  markSolution = 'markSolution',
  lockedPost = 'lockedPost',
  threadSubmission = 'threadSubmission',
  postSubmission = 'postSubmission',
  notGroupMember = 'notGroupMember',
  pinThread = 'pinThread',

  // Server validation errors
  actionOnDeletedPost = 'actionOnDeletedPost',
  actionForbidden = 'actionForbidden',
  updateWindowCrossed = 'updateWindowCrossed',
  unauthorizedAction = 'unauthorizedAction',
  unauthorizedMarketplaceId = 'unauthorizedMarketplaceId',
  opNotSameMarketplace = 'opNotSameMarketplace',
  opContainsReplies = 'opContainsReplies',
  cannotUpdateToDraft = 'cannotUpdateToDraft',
  postIsMarkedAsSolution = 'postIsMarkedAsSolution',
  silenced = 'silenced',
  invalidContent = 'invalidContent',
  hasNoAccessToPrivateDiscussion = 'hasNoAccessToPrivateDiscussion',
}

export enum PostSuccess {
  copyPost = 'copyPost',
  deletePost = 'deletePost',
}

export enum PostBanner {
  personInformation = 'PII',
  inappropriateContent = 'INAPPROPRIATE_CONTENT',
  lockedThread = 'LOCKED_THREAD',
}

export enum MarkSolutionOptions {
  mark = 'MARK',
  unmark = 'UNMARK',
}

export interface Mention {
  userId: string,
  username: string,
}

export enum QuoteState {
  updated = 'UPDATED',
  deleted = 'DELETED',
}
export interface Quote {
  userId: string,
  username: string,
  content: string,
  avatarSrc: string,
  postId: string,
  state?: QuoteState
}

export interface Quotes { [quoteId: string]: Quote }

export interface Mentions { [mentionId: string]: Mention }

export interface ContentMetadata {
  mentions?: Mentions;
  quotes?: Quotes;
}

export interface Post {
  threadId: string;
  id: string;
  content?: string;
  username: string;
  userId?: string;
  postType: PostType;
  postState: PostState;
  createdAt: Date;
  updatedAt?: Date;
  upVoteCount: number;
  downVoteCount: number;
  replyCount: number;
  avatarSrc: string;
  banners?: Array<PostBanner>;
  isLocked?: boolean;
  isMigrated?: boolean;
  isFlagged?: boolean;
  isDeleted?: boolean;
  parentPostId?: string;
  voteDecision?: VoteOptions;
  updatedByModerator?: boolean;
  isAmazonReplied?: boolean;
  contentMetadata?: ContentMetadata;
  marketplaceId: string;
  pinnedToCategory?: boolean;
  pinnedToGroup?: boolean;
  pinnedToListing?: boolean;
}

export interface ReplyPost extends Post {
  replyToId: string;
  replyToUsername: string;
  isSolution?: boolean;
}

export interface OpPost extends Post {
  title: string;
  viewCount: number;
  tags?: Array<string>;
  categoryId: string;
  groupName?: string;
  groupId?: string;
  solutionId?: string;
}

export interface ThreadDetailPostState {
  // handling error experiences
  errors: Array<PostErrors>;
  // handling success experiences
  success: Array<PostSuccess>;
  childrenPostIds: Array<string>;
  isPreviousChildrenPostsLoading?: boolean;
  isNextChildrenPostsLoading?: boolean;
  previousCursor?: string;
  nextCursor?: string;
  showReplies?: boolean;
  postDetails: ReplyPost | OpPost
}

export interface ThreadDetailResponse {
  posts: {
    previousCursor?: string;
    nextCursor?: string;
    entries: Array<ReplyPost | OpPost>;
  }
  repliesMap?: {
    [parentPostId: string]: {
      previousCursor?: string;
      nextCursor?: string;
      entries: Array<ReplyPost>;
    }
  }
  repliesCount?: number
  solution?: ReplyPost
}

export interface PostRepliesResponse {
  replies: {
    previousCursor?: string;
    nextCursor?: string;
    parentPostId: string;
    entries: Array<ReplyPost>;
  }
}

export interface GetPostResponse {
  post: OpPost | ReplyPost
}

export enum CursorDirection {
  previous = 'PREVIOUS',
  next = 'NEXT',
}

export interface ThreadDetailRequest {
  threadId: string;
  cursor?: string;
  sortBy?: SortOptions;
  filterBy?: FilterOptions;
  direction?: CursorDirection;
  postId?: string;
}

export interface ThreadDetailSubmitVoteRequest {
  threadId: string;
  postId: string;
  voteDecision: VoteOptions;
}

export interface ThreadDetailSubmitVoteResponse {
  upVoteCount: number;
  downVoteCount: number;
}

export interface PostRepliesRequest {
  threadId: string;
  postId: string;
  cursor?: string;
  direction?: CursorDirection;
  filterBy: FilterOptions
}

export interface DeletePostRequest {
  threadId: string;
  postId: string
}

export interface MarkSolutionRequest {
  threadId: string;
  postId: string;
  solutionAction: MarkSolutionOptions;
}

export interface ThreadDetailSubmitPostRequest {
  threadId: string;
  // @todo Change id to postId
  id?: string;
  inReplyPostId: string;
  draft: boolean;
  content: string;
  type?: DraftPostType;
  contentMetadata: ElementsMetadata,
}

export interface GetDraftRequest {
  threadId: string;
}

export interface GetDraftResponse {
  draft: {
    content?: string;
    contentMetadata?: ContentMetadata,
    id?: string
  },
  inReplyPost: OpPost | ReplyPost | {}
}

export interface PinInfoRequest {
  threadId: string;
}

export interface PinInfoResponse {
  pinnableToCategory: boolean;
  pinnableToGroup: boolean;
  pinnableToListing: boolean;
}

export interface GetPinsForThreadRequest {
  threadId: string;
}

export interface GetPinsForThreadResponse {
  pinnedToCategory: boolean;
  pinnedToGroup: boolean;
  pinnedToListing: boolean;
}

export interface UpdatePinsForThreadRequest {
  threadId: string;
  pinnedToCategory: boolean;
  pinnedToGroup: boolean;
  pinnedToListing: boolean;
}

export interface UpdatePinsForThreadResponse {
  pinnedToCategory: boolean;
  pinnedToGroup: boolean;
  pinnedToListing: boolean;
}

export interface ThreadDetailSubmitPostResponse {
  post: ReplyPost
}

export enum VoteOptions {
  upVote = 'UPVOTE',
  downVote = 'DOWNVOTE',
  unVote = 'NONE',
}

export enum FilterOptions {
  allReplies = 'ALL',
  myReplies = 'MY_REPLIES',
  amazonReplies = 'AMAZON_REPLIES',
}

export enum SortOptions {
  oldest = 'OLDEST',
  newest = 'NEWEST',
  mostVotes = 'MOST_VOTES',
}

export enum DraftPostType {
  reply = 'reply',
  edit = 'edit',
}

export interface FlagPostRequest {
  postId: string,
  flagType: FlagPostOptions
}
